import { Node, Edge } from '@xyflow/react';

export interface IndustryFlowBlueprint {
  id: string;
  title: string;
  category: 'ecommerce' | 'healthcare' | 'support' | 'corporate';
  badge: string;
  badgeColor: string;
  iconName: string;
  description: string;
  highlight: string;
  nodes: Node[];
  edges: Edge[];
}

export const INDUSTRY_BLUEPRINTS: IndustryFlowBlueprint[] = [
  // ── 1. 🛒 E-Commerce & WhatsApp Catalog Bot ──
  {
    id: 'blueprint-ecommerce-catalog',
    title: 'E-Commerce & WhatsApp Catalog Bot',
    category: 'ecommerce',
    badge: 'RETAIL & COMMERCE',
    badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40',
    iconName: 'ShoppingBag',
    description: 'Autonomous WhatsApp assistant for item browsing, pricing, discount calculations, payment links, and instant VIP manager alerts.',
    highlight: 'Includes Multimodal Vision Catalog, PayHere/Stripe links & VIP alert dispatch.',
    nodes: [
      {
        id: 'node-luna-core',
        type: 'lunaCoreNode',
        position: { x: 560, y: 240 },
        data: {
          agentName: 'Luna Store Assistant',
          avatarFaceId: 'cute_chibi_bot',
          baseVoice: 'Puck',
          languageMode: 'bilingual',
          naturalSpokenSinhala: true,
          temperature: 0.6,
          isActive: true,
          isCustomizable: true,
        },
      },
      {
        id: 'node-persona-ecommerce',
        type: 'instructionNode',
        position: { x: 60, y: 60 },
        data: {
          personaTitle: 'Retail Sales & Order Specialist',
          instructions: 'You are Luna, an enthusiastic and helpful sales co-pilot. Assist shoppers in Sinhala, Singlish, and English. Present product specs, check discounts, take orders, and send photos/brochures to WhatsApp.',
          temperature: 0.6,
          isCustomizable: true,
        },
      },
      {
        id: 'node-catalog-ecommerce',
        type: 'productCatalogNode',
        position: { x: 60, y: 380 },
        data: {
          defaultCurrency: 'LKR',
          allowAiDiscounts: true,
          maxDiscountPercent: 10,
          products: [
            {
              id: 'item-1',
              name: 'Premium Leather Smart Bag',
              price: 8500,
              originalPrice: 9500,
              category: 'Fashion',
              inStock: true,
              description: 'Waterproof luxury leather travel bag with USB charging port.',
              keywords: 'bag, leather bag, smart bag, loku bag, bag eka',
            },
            {
              id: 'item-2',
              name: 'Wireless Noise-Cancelling Earbuds',
              price: 6200,
              originalPrice: 7500,
              category: 'Electronics',
              inStock: true,
              description: 'ANC wireless earbuds with 36hr battery backup and touch controls.',
              keywords: 'earphone, earbuds, bluetooth, pod, wireless audio',
            },
          ],
          isCustomizable: true,
        },
      },
      {
        id: 'node-media-ecommerce',
        type: 'mediaVaultNode',
        position: { x: 60, y: 720 },
        data: {
          folderName: 'Product Catalogs & Demos',
          autoSync: true,
          isCustomizable: true,
        },
      },
      {
        id: 'node-whatsapp-ecommerce',
        type: 'whatsappInstanceNode',
        position: { x: 1100, y: 60 },
        data: {
          instanceLabel: 'WhatsApp Commerce Gateway',
          enableSipCalling: true,
          antiBanEnabled: true,
          antiBanTypingSpeed: 'natural',
          isCustomizable: true,
        },
      },
      {
        id: 'node-payment-ecommerce',
        type: 'paymentGatewayNode',
        position: { x: 1100, y: 380 },
        data: {
          provider: 'bank_transfer',
          bankName: 'Commercial Bank',
          accountNumber: '8001234567',
          accountHolder: 'Kangavena Retail',
          branch: 'Colombo 03',
          enablePaymentLinks: true,
          isCustomizable: true,
        },
      },
      {
        id: 'node-intent-ecommerce',
        type: 'intentAlertNode',
        position: { x: 1100, y: 720 },
        data: {
          notificationChannel: 'whatsapp',
          rules: [
            {
              id: 'rule-high-value',
              name: 'High-Value Order Request',
              triggerKeywords: 'buy, order, purchase, ganne kohomada, deliver karanna, price eka adu karanna',
              destination: '+94771234567',
              priority: 'high',
            },
          ],
          isCustomizable: true,
        },
      },
    ],
    edges: [
      {
        id: 'e-instruction-core',
        source: 'node-persona-ecommerce',
        sourceHandle: 'out-instruction',
        target: 'node-luna-core',
        targetHandle: 'in-instructions',
        type: 'default',
        style: { stroke: '#38bdf8', strokeWidth: 2.5 },
      },
      {
        id: 'e-catalog-core',
        source: 'node-catalog-ecommerce',
        sourceHandle: 'out-catalog',
        target: 'node-luna-core',
        targetHandle: 'in-knowledge',
        type: 'default',
        style: { stroke: '#10b981', strokeWidth: 2.5 },
      },
      {
        id: 'e-media-core',
        source: 'node-media-ecommerce',
        sourceHandle: 'out-media-vault',
        target: 'node-luna-core',
        targetHandle: 'in-knowledge',
        type: 'default',
        style: { stroke: '#10b981', strokeWidth: 2.5 },
      },
      {
        id: 'e-core-whatsapp-out',
        source: 'node-luna-core',
        sourceHandle: 'out-whatsapp',
        target: 'node-whatsapp-ecommerce',
        targetHandle: 'in-whatsapp',
        type: 'default',
        style: { stroke: '#10b981', strokeWidth: 2.5 },
      },
      {
        id: 'e-whatsapp-core-in',
        source: 'node-whatsapp-ecommerce',
        sourceHandle: 'out-whatsapp-instance',
        target: 'node-luna-core',
        targetHandle: 'in-core',
        type: 'default',
        style: { stroke: '#10b981', strokeWidth: 2.5 },
      },
      {
        id: 'e-core-payment',
        source: 'node-luna-core',
        sourceHandle: 'out-pos',
        target: 'node-payment-ecommerce',
        targetHandle: 'in-payment',
        type: 'default',
        style: { stroke: '#10b981', strokeWidth: 2.5 },
      },
      {
        id: 'e-core-intent',
        source: 'node-luna-core',
        sourceHandle: 'out-intent',
        target: 'node-intent-ecommerce',
        targetHandle: 'in-intent',
        type: 'default',
        style: { stroke: '#f43f5e', strokeWidth: 2.5 },
      },
    ],
  },

  // ── 2. 🏥 Clinic & Service Appointment Concierge ──
  {
    id: 'blueprint-clinic-appointments',
    title: 'Clinic & Service Appointment Concierge',
    category: 'healthcare',
    badge: 'HEALTHCARE & CLINICS',
    badgeColor: 'bg-cyan-500/20 text-cyan-300 border-cyan-400/40',
    iconName: 'HeartPulse',
    description: 'Empathetic patient receptionist for doctor booking, consultation fee queries, patient symptom intake, and emergency nurse call transfers.',
    highlight: 'Includes Patient Voice Intake Form, Doctor Schedules RAG & SIP Live Call Transfer.',
    nodes: [
      {
        id: 'node-luna-core',
        type: 'lunaCoreNode',
        position: { x: 560, y: 240 },
        data: {
          agentName: 'Luna Clinic Concierge',
          avatarFaceId: 'nurse_ai_avatar',
          baseVoice: 'Aoede',
          languageMode: 'bilingual',
          naturalSpokenSinhala: true,
          temperature: 0.5,
          isActive: true,
          isCustomizable: true,
        },
      },
      {
        id: 'node-persona-clinic',
        type: 'instructionNode',
        position: { x: 60, y: 60 },
        data: {
          personaTitle: 'Medical Concierge & Receptionist',
          instructions: 'You are Luna, an empathetic, calm medical receptionist. Guide patients through booking appointments with visiting doctors, answer consultation questions, collect patient details, and transfer emergencies immediately.',
          temperature: 0.5,
          isCustomizable: true,
        },
      },
      {
        id: 'node-knowledge-clinic',
        type: 'knowledgeNode',
        position: { x: 60, y: 380 },
        data: {
          knowledgeType: 'faq',
          contextSource: 'Dr. Perera (Cardiologist) - Mon/Wed/Fri 5:00 PM - Fee: Rs. 2,500.\nDr. Fernando (Pediatrician) - Tue/Thu/Sat 9:00 AM - Fee: Rs. 2,000.\nDr. Jayawardena (Dermatologist) - Daily 4:00 PM - Fee: Rs. 2,200.\nEmergency services open 24/7. Lab tests report collection by 2:00 PM.',
          autoSync: true,
          isCustomizable: true,
        },
      },
      {
        id: 'node-memory-clinic',
        type: 'lunaMemoryNode',
        position: { x: 60, y: 720 },
        data: {
          memoryLabel: 'Patient History & Recall',
          retentionDays: 180,
          isCustomizable: true,
        },
      },
      {
        id: 'node-form-clinic',
        type: 'formGeneratorNode',
        position: { x: 1100, y: 60 },
        data: {
          formTitle: 'Doctor Appointment Booking',
          fields: [
            { id: 'f1', label: 'Patient Name', type: 'text', required: true },
            { id: 'f2', label: 'Phone Number', type: 'phone', required: true },
            { id: 'f3', label: 'Doctor / Specialty', type: 'text', required: true },
            { id: 'f4', label: 'Preferred Date & Time', type: 'datetime', required: true },
            { id: 'f5', label: 'Symptoms / Notes', type: 'textarea', required: false },
          ],
          isCustomizable: true,
        },
      },
      {
        id: 'node-transfer-clinic',
        type: 'callTransferNode',
        position: { x: 1100, y: 400 },
        data: {
          transferPhoneNumber: '+94770001122',
          agentName: 'Duty Medical Officer / Emergency Nurse',
          whisperMessage: 'Urgent incoming emergency patient transfer from Luna AI.',
          isCustomizable: true,
        },
      },
      {
        id: 'node-action-clinic',
        type: 'actionNode',
        position: { x: 1100, y: 720 },
        data: {
          actionType: 'whatsapp_lead',
          targetDestination: '+94770001122',
          isCustomizable: true,
        },
      },
    ],
    edges: [
      {
        id: 'e-clinic-instruction',
        source: 'node-persona-clinic',
        sourceHandle: 'out-instruction',
        target: 'node-luna-core',
        targetHandle: 'in-instructions',
        type: 'default',
        style: { stroke: '#38bdf8', strokeWidth: 2.5 },
      },
      {
        id: 'e-clinic-knowledge',
        source: 'node-knowledge-clinic',
        sourceHandle: 'out-knowledge',
        target: 'node-luna-core',
        targetHandle: 'in-knowledge',
        type: 'default',
        style: { stroke: '#10b981', strokeWidth: 2.5 },
      },
      {
        id: 'e-clinic-memory',
        source: 'node-memory-clinic',
        sourceHandle: 'out-memory',
        target: 'node-luna-core',
        targetHandle: 'in-memory',
        type: 'default',
        style: { stroke: '#c084fc', strokeWidth: 2.5 },
      },
      {
        id: 'e-clinic-form',
        source: 'node-luna-core',
        sourceHandle: 'out-actions',
        target: 'node-form-clinic',
        targetHandle: 'in-form-doc',
        type: 'default',
        style: { stroke: '#a855f7', strokeWidth: 2.5 },
      },
      {
        id: 'e-clinic-transfer',
        source: 'node-luna-core',
        sourceHandle: 'out-whatsapp',
        target: 'node-transfer-clinic',
        targetHandle: 'in-transfer',
        type: 'default',
        style: { stroke: '#10b981', strokeWidth: 2.5 },
      },
      {
        id: 'e-clinic-action',
        source: 'node-luna-core',
        sourceHandle: 'out-actions',
        target: 'node-action-clinic',
        targetHandle: 'in-action',
        type: 'default',
        style: { stroke: '#a855f7', strokeWidth: 2.5 },
      },
    ],
  },

  // ── 3. 📞 24/7 Corporate Customer Care & Voice SIP Gateway ──
  {
    id: 'blueprint-corporate-sip-support',
    title: '24/7 Corporate Support & Voice SIP Gateway',
    category: 'support',
    badge: 'ENTERPRISE TELEPHONY',
    badgeColor: 'bg-purple-500/20 text-purple-300 border-purple-400/40',
    iconName: 'Headphones',
    description: 'High-throughput enterprise call center agent with live SIP two-way audio, call duration limits, spam filtering, human agent escalations, and ticket emails.',
    highlight: 'Includes 5-min Call Guard Watchdog, Security Prompt-Shield & Automated Email SMTP.',
    nodes: [
      {
        id: 'node-luna-core',
        type: 'lunaCoreNode',
        position: { x: 560, y: 240 },
        data: {
          agentName: 'Enterprise Luna Co-Pilot',
          avatarFaceId: 'corporate_agent_3d',
          baseVoice: 'Charon',
          languageMode: 'bilingual',
          naturalSpokenSinhala: true,
          temperature: 0.4,
          isActive: true,
          isCustomizable: true,
        },
      },
      {
        id: 'node-persona-support',
        type: 'instructionNode',
        position: { x: 60, y: 40 },
        data: {
          personaTitle: 'Corporate Client Solutions Specialist',
          instructions: 'You are Luna, representing enterprise customer relations. Provide prompt, accurate technical and billing assistance. Uphold strict company confidentiality, handle complaints professionally, and escalate when requested.',
          temperature: 0.4,
          isCustomizable: true,
        },
      },
      {
        id: 'node-knowledge-support',
        type: 'knowledgeNode',
        position: { x: 60, y: 320 },
        data: {
          knowledgeType: 'policy',
          contextSource: 'SLA Response Time: 1 hour for critical tickets, 4 hours for standard tickets.\nWarranty Policy: 1-year comprehensive replacement on hardware defects.\nBilling: Invoices issued on 1st of each month with 14-day net payment terms.\nSupport Hotlines: Colombo (+94 11 200 3000), Kandy (+94 81 200 3000).',
          autoSync: true,
          isCustomizable: true,
        },
      },
      {
        id: 'node-callguard-support',
        type: 'callGuardNode',
        position: { x: 60, y: 600 },
        data: {
          maxCallDurationMinutes: 5,
          silenceTimeoutSeconds: 15,
          enableSpamBlocker: true,
          disconnectMessage: 'Thank you for calling Kangavena Corporate. Have a great day!',
          isCustomizable: true,
        },
      },
      {
        id: 'node-security-support',
        type: 'securityFilterNode',
        position: { x: 60, y: 880 },
        data: {
          forbiddenTopics: 'internal server architecture, database passwords, employee personal salaries',
          antiHallucination: true,
          piiShield: true,
          isCustomizable: true,
        },
      },
      {
        id: 'node-whatsapp-support',
        type: 'whatsappInstanceNode',
        position: { x: 1100, y: 40 },
        data: {
          instanceLabel: 'Live SIP Support Line',
          enableSipCalling: true,
          sipAction: 'luna_live_answer',
          antiBanEnabled: true,
          isCustomizable: true,
        },
      },
      {
        id: 'node-transfer-support',
        type: 'callTransferNode',
        position: { x: 1100, y: 360 },
        data: {
          transferPhoneNumber: '+94112003000',
          agentName: 'Tier-2 Technical Support Team',
          whisperMessage: 'Customer requesting human agent escalation regarding technical SLA.',
          isCustomizable: true,
        },
      },
      {
        id: 'node-email-support',
        type: 'emailSmtpNode',
        position: { x: 1100, y: 680 },
        data: {
          fromEmail: 'support@kangavena.com',
          senderName: 'Kangavena Corporate Helpdesk',
          subjectTemplate: 'Support Ticket Confirmation - Case #{{ticketId}}',
          isCustomizable: true,
        },
      },
    ],
    edges: [
      {
        id: 'e-support-instruction',
        source: 'node-persona-support',
        sourceHandle: 'out-instruction',
        target: 'node-luna-core',
        targetHandle: 'in-instructions',
        type: 'default',
        style: { stroke: '#38bdf8', strokeWidth: 2.5 },
      },
      {
        id: 'e-support-knowledge',
        source: 'node-knowledge-support',
        sourceHandle: 'out-knowledge',
        target: 'node-luna-core',
        targetHandle: 'in-knowledge',
        type: 'default',
        style: { stroke: '#10b981', strokeWidth: 2.5 },
      },
      {
        id: 'e-support-callguard',
        source: 'node-callguard-support',
        sourceHandle: 'out-callguard',
        target: 'node-luna-core',
        targetHandle: 'in-security',
        type: 'default',
        style: { stroke: '#f59e0b', strokeWidth: 2.5 },
      },
      {
        id: 'e-support-security',
        source: 'node-security-support',
        sourceHandle: 'out-security',
        target: 'node-luna-core',
        targetHandle: 'in-security',
        type: 'default',
        style: { stroke: '#f59e0b', strokeWidth: 2.5 },
      },
      {
        id: 'e-support-whatsapp-out',
        source: 'node-luna-core',
        sourceHandle: 'out-whatsapp',
        target: 'node-whatsapp-support',
        targetHandle: 'in-whatsapp',
        type: 'default',
        style: { stroke: '#10b981', strokeWidth: 2.5 },
      },
      {
        id: 'e-support-whatsapp-in',
        source: 'node-whatsapp-support',
        sourceHandle: 'out-whatsapp-instance',
        target: 'node-luna-core',
        targetHandle: 'in-core',
        type: 'default',
        style: { stroke: '#10b981', strokeWidth: 2.5 },
      },
      {
        id: 'e-support-transfer',
        source: 'node-luna-core',
        sourceHandle: 'out-whatsapp',
        target: 'node-transfer-support',
        targetHandle: 'in-transfer',
        type: 'default',
        style: { stroke: '#10b981', strokeWidth: 2.5 },
      },
      {
        id: 'e-support-email',
        source: 'node-luna-core',
        sourceHandle: 'out-actions',
        target: 'node-email-support',
        targetHandle: 'in-email',
        type: 'default',
        style: { stroke: '#a855f7', strokeWidth: 2.5 },
      },
    ],
  },
];
